import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Ban,
  BarChart3,
  CheckCircle2,
  Download,
  Info,
  Loader2,
  MoreVertical,
  Pencil,
  Phone,
  RefreshCw,
  Unlock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  fetchPbmDirectory,
  setPbmBlockedNumber,
  updatePbmPcnPayerName,
  type PbmDirectoryQuery,
} from "@/lib/pbm-directory-api";
import { SEARCH_DEBOUNCE_MS } from "@/lib/constants";
import type { PbmContactRow, PbmDirectoryEntry } from "@/types/pbm-directory";
import { cn } from "@/utils";

type Quality = "high" | "medium" | "low" | "none";

type PbmDirectoryQualityFilter = "all" | "high" | "medium" | "low";

function sumAttempts(contacts: PbmContactRow[]) {
  let succ = 0;
  let fail = 0;
  for (const c of contacts) {
    succ += Number(c.successfulAttempts ?? 0);
    fail += Number(c.unsuccessfulAttempts ?? 0);
  }
  const total = succ + fail;
  const rate = total > 0 ? succ / total : null;
  return { succ, fail, total, rate };
}

function rowAttempts(c: PbmContactRow) {
  const succ = Number(c.successfulAttempts ?? 0);
  const fail = Number(c.unsuccessfulAttempts ?? 0);
  const total = succ + fail;
  const rate = total > 0 ? succ / total : null;
  return { succ, fail, total, rate };
}

/** Best success rate first; rows with no attempts last; then more successes / attempts, then phone. */
function compareContactsBySuccessRate(a: PbmContactRow, b: PbmContactRow): number {
  const ra = rowAttempts(a);
  const rb = rowAttempts(b);
  if (ra.rate !== null && rb.rate !== null && rb.rate !== ra.rate) {
    return rb.rate - ra.rate;
  }
  if (ra.rate !== null && rb.rate === null) return -1;
  if (ra.rate === null && rb.rate !== null) return 1;
  if (rb.succ !== ra.succ) return rb.succ - ra.succ;
  if (rb.total !== ra.total) return rb.total - ra.total;
  return (a.phoneNumber ?? "")
    .trim()
    .localeCompare((b.phoneNumber ?? "").trim(), undefined, { sensitivity: "base" });
}

function qualityFromRate(rate: number | null): Quality {
  if (rate === null) return "none";
  if (rate >= 0.7) return "high";
  if (rate >= 0.4) return "medium";
  return "low";
}

function entryOverallQuality(entry: PbmDirectoryEntry): Quality {
  return qualityFromRate(sumAttempts(entry.contacts).rate);
}

function entryTotalAttempts(entry: PbmDirectoryEntry): number {
  return sumAttempts(entry.contacts).total;
}

/** Most attempts first; stable tie-breakers for equal totals. */
function comparePbmEntriesByAttempts(a: PbmDirectoryEntry, b: PbmDirectoryEntry): number {
  const diff = entryTotalAttempts(b) - entryTotalAttempts(a);
  if (diff !== 0) return diff;
  const pa = (a.payer_name ?? "").trim();
  const pb = (b.payer_name ?? "").trim();
  const byPayer = pa.localeCompare(pb, undefined, { sensitivity: "base" });
  if (byPayer !== 0) return byPayer;
  const byBin = a.member_bin.localeCompare(b.member_bin, undefined, { sensitivity: "base" });
  if (byBin !== 0) return byBin;
  return a.member_pcn.localeCompare(b.member_pcn, undefined, { sensitivity: "base" });
}

function groupTotalAttempts(group: PbmPayerGroup): number {
  let total = 0;
  for (const e of group.entries) {
    total += entryTotalAttempts(e);
  }
  return total;
}

/** Exact trimmed PCN-level payer name; empty string groups as unnamed. */
function payerDirectoryGroupKey(entry: PbmDirectoryEntry): string {
  return (entry.payer_name ?? "").trim();
}

type PbmPayerGroup = {
  groupKey: string;
  displayLabel: string;
  entries: PbmDirectoryEntry[];
};

function buildPayerGroups(entries: PbmDirectoryEntry[]): PbmPayerGroup[] {
  const map = new Map<string, PbmDirectoryEntry[]>();
  for (const e of entries) {
    const k = payerDirectoryGroupKey(e);
    const list = map.get(k);
    if (list) list.push(e);
    else map.set(k, [e]);
  }
  const groups: PbmPayerGroup[] = [];
  for (const [groupKey, rawEntries] of map) {
    rawEntries.sort(comparePbmEntriesByAttempts);
    groups.push({
      groupKey,
      displayLabel: groupKey ? groupKey : "Unnamed payer",
      entries: rawEntries,
    });
  }
  groups.sort((a, b) => {
    const byAttempts = groupTotalAttempts(b) - groupTotalAttempts(a);
    if (byAttempts !== 0) return byAttempts;
    return a.displayLabel.localeCompare(b.displayLabel, undefined, { sensitivity: "base" });
  });
  return groups;
}

function formatPct(rate: number | null): string {
  if (rate === null) return "—";
  return `${Math.round(rate * 1000) / 10}%`;
}

const qualityLabel: Record<Quality, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
  none: "N/A",
};

const qualityBadgeClass: Record<Quality, string> = {
  high: "border-emerald-500/50 bg-emerald-500/[0.08] text-emerald-800 dark:text-emerald-300",
  medium: "border-amber-500/50 bg-amber-500/[0.08] text-amber-900 dark:text-amber-200",
  low: "border-red-500/45 bg-red-500/[0.08] text-red-700 dark:text-red-400",
  none: "border-border bg-muted/40 text-muted-foreground",
};

const phoneQualityClass: Record<Quality, string> = {
  high: "text-emerald-700 dark:text-emerald-400",
  medium: "text-amber-700 dark:text-amber-300",
  low: "text-red-600 dark:text-red-400",
  none: "text-muted-foreground",
};

function QualityBadge({ q, className }: { q: Quality; className?: string }) {
  const Icon =
    q === "high" ? CheckCircle2 : q === "medium" ? Info : q === "low" ? AlertTriangle : null;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold leading-none",
        qualityBadgeClass[q],
        className,
      )}
    >
      {Icon ? <Icon className="h-3 w-3 shrink-0 opacity-90" aria-hidden /> : null}
      {qualityLabel[q]}
    </span>
  );
}

function copyToClipboard(text: string) {
  if (!text) return;
  void navigator.clipboard.writeText(text);
}

function PayerNameGroupBlock({
  group,
  onRefresh,
}: {
  group: PbmPayerGroup;
  onRefresh: () => void | Promise<void>;
}) {
  const [editingPayerName, setEditingPayerName] = useState(false);
  const [payerNameDraft, setPayerNameDraft] = useState("");
  const [payerNameSaving, setPayerNameSaving] = useState(false);
  const [groupError, setGroupError] = useState<string | null>(null);

  const beginEditPayerName = useCallback(() => {
    setGroupError(null);
    setPayerNameDraft(group.groupKey);
    setEditingPayerName(true);
  }, [group.groupKey]);

  const cancelEditPayerName = useCallback(() => {
    setEditingPayerName(false);
    setPayerNameDraft("");
  }, []);

  const savePayerName = useCallback(async () => {
    setGroupError(null);
    setPayerNameSaving(true);
    const nextName = payerNameDraft.trim();
    try {
      const results = await Promise.all(
        group.entries.map((e) =>
          updatePbmPcnPayerName({
            member_bin: e.member_bin,
            member_pcn: e.member_pcn,
            payer_name: nextName,
          }),
        ),
      );
      const failed = results.filter((r) => !r.success);
      if (failed.length > 0) {
        setGroupError(failed[0]?.error || `${failed.length} update(s) failed`);
        return;
      }
      setEditingPayerName(false);
      await onRefresh();
    } finally {
      setPayerNameSaving(false);
    }
  }, [group.entries, payerNameDraft, onRefresh]);

  const subsectionCount = group.entries.length;

  return (
    <section
      className="overflow-hidden rounded-xl border border-border/80 bg-card shadow-sm"
      aria-label={`Payer group: ${group.displayLabel}`}
    >
      <div className="border-b border-border/60 bg-muted/25 px-6 py-5 sm:px-8">
        {groupError ? (
          <p className="mb-3 text-sm text-destructive" role="alert">
            {groupError}
          </p>
        ) : null}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                PCN-level payer name
              </p>
              {!editingPayerName ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
                  onClick={beginEditPayerName}
                >
                  <Pencil className="h-3 w-3" aria-hidden />
                  Edit
                </Button>
              ) : null}
            </div>
            {editingPayerName ? (
              <div className="flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-center">
                <Input
                  value={payerNameDraft}
                  onChange={(e) => setPayerNameDraft(e.target.value)}
                  placeholder="Payer / plan name"
                  autoComplete="organization"
                  className="max-w-xl font-medium"
                  disabled={payerNameSaving}
                  aria-label="PCN-level payer name for this group"
                />
                <div className="flex shrink-0 gap-2">
                  <Button
                    type="button"
                    size="sm"
                    disabled={payerNameSaving}
                    onClick={() => void savePayerName()}
                    className="gap-1"
                  >
                    {payerNameSaving ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    ) : null}
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={payerNameSaving}
                    onClick={cancelEditPayerName}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-xl font-bold leading-snug tracking-tight text-foreground sm:text-2xl">
                {group.displayLabel}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              {subsectionCount} BIN / PCN {subsectionCount === 1 ? "row" : "rows"} with this payer name
              {subsectionCount > 1
                ? " — header Save updates every row below. To change one BIN/PCN only, use Edit on that card."
                : ""}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 p-4 sm:p-6">
        {group.entries.map((e) => (
          <BinPcnSubsection
            key={`${e.member_bin}-${e.member_pcn}`}
            entry={e}
            onRefresh={onRefresh}
          />
        ))}
      </div>
    </section>
  );
}

function BinPcnSubsection({
  entry,
  onRefresh,
}: {
  entry: PbmDirectoryEntry;
  onRefresh: () => void | Promise<void>;
}) {
  const [pendingPhone, setPendingPhone] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editingPcnPayerName, setEditingPcnPayerName] = useState(false);
  const [pcnPayerDraft, setPcnPayerDraft] = useState("");
  const [pcnPayerSaving, setPcnPayerSaving] = useState(false);
  const [showBlockedInSection, setShowBlockedInSection] = useState(false);
  const agg = sumAttempts(entry.contacts);
  const aggQ = entryOverallQuality(entry);
  const pcnPayerDisplay = entry.payer_name?.trim() || "—";

  const contactsSortedByRate = useMemo(
    () => [...entry.contacts].sort(compareContactsBySuccessRate),
    [entry.contacts],
  );

  const blockedContactCount = useMemo(
    () => entry.contacts.filter((c) => Boolean(c.blocked)).length,
    [entry.contacts],
  );

  const visibleContacts = useMemo(() => {
    if (showBlockedInSection) return contactsSortedByRate;
    return contactsSortedByRate.filter((c) => !c.blocked);
  }, [contactsSortedByRate, showBlockedInSection]);

  useEffect(() => {
    if (blockedContactCount === 0) setShowBlockedInSection(false);
  }, [blockedContactCount]);
  const beginEditPcnPayerName = useCallback(() => {
    setActionError(null);
    setPcnPayerDraft(entry.payer_name ?? "");
    setEditingPcnPayerName(true);
  }, [entry.payer_name]);

  const cancelEditPcnPayerName = useCallback(() => {
    setEditingPcnPayerName(false);
    setPcnPayerDraft("");
  }, []);

  const savePcnPayerName = useCallback(async () => {
    setActionError(null);
    setPcnPayerSaving(true);
    try {
      const out = await updatePbmPcnPayerName({
        member_bin: entry.member_bin,
        member_pcn: entry.member_pcn,
        payer_name: pcnPayerDraft.trim(),
      });
      if (!out.success) {
        setActionError(out.error || "Update failed");
        return;
      }
      setEditingPcnPayerName(false);
      await onRefresh();
    } finally {
      setPcnPayerSaving(false);
    }
  }, [entry.member_bin, entry.member_pcn, pcnPayerDraft, onRefresh]);

  const toggleBlocked = useCallback(
    async (phone: string, currentlyBlocked: boolean) => {
      setActionError(null);
      setPendingPhone(phone);
      try {
        const out = await setPbmBlockedNumber({
          member_bin: entry.member_bin,
          member_pcn: entry.member_pcn,
          phone_number: phone,
          blocked: !currentlyBlocked,
        });
        if (!out.success) {
          setActionError(out.error || "Update failed");
          return;
        }
        await onRefresh();
      } finally {
        setPendingPhone(null);
      }
    },
    [entry.member_bin, entry.member_pcn, onRefresh],
  );

  return (
    <div
      className="overflow-hidden rounded-lg border border-border/70 bg-background/80 shadow-sm"
      aria-label={`BIN ${entry.member_bin} PCN ${entry.member_pcn}`}
    >
      <div className="border-b border-border/50 bg-muted/15 px-4 py-4 sm:px-6 sm:py-5">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          BIN / PCN
        </p>
        <div className="mt-2 flex min-w-0 flex-wrap items-end gap-x-8 gap-y-3">
          <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Member BIN
            </p>
            <p className="font-mono text-lg font-bold tracking-tight text-foreground sm:text-xl">
              {entry.member_bin}
            </p>
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Member PCN
            </p>
            <p className="font-mono text-lg font-bold tracking-tight text-foreground sm:text-xl">
              {entry.member_pcn}
            </p>
          </div>
        </div>

        <div className="mt-4 border-t border-border/40 pt-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div className="min-w-0 flex-1 space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  PCN payer name (this BIN / PCN only)
                </p>
                {!editingPcnPayerName ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
                    onClick={beginEditPcnPayerName}
                  >
                    <Pencil className="h-3 w-3" aria-hidden />
                    Edit
                  </Button>
                ) : null}
              </div>
              {editingPcnPayerName ? (
                <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center">
                  <Input
                    value={pcnPayerDraft}
                    onChange={(e) => setPcnPayerDraft(e.target.value)}
                    placeholder="Payer / plan name for this BIN+PCN"
                    autoComplete="organization"
                    className="max-w-lg text-sm font-medium"
                    disabled={pcnPayerSaving}
                    aria-label="PCN-level payer name for this BIN and PCN only"
                  />
                  <div className="flex shrink-0 gap-2">
                    <Button
                      type="button"
                      size="sm"
                      disabled={pcnPayerSaving}
                      onClick={() => void savePcnPayerName()}
                      className="gap-1"
                    >
                      {pcnPayerSaving ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                      ) : null}
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={pcnPayerSaving}
                      onClick={cancelEditPcnPayerName}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm sm:gap-x-8">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Overall quality
            </p>
            <QualityBadge q={aggQ} />
          </div>
          <span className="inline-flex items-center gap-2 text-muted-foreground">
            <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" aria-hidden />
            <span className="font-semibold tabular-nums text-foreground">{agg.succ}</span>
            <span>successful</span>
          </span>
          <span className="inline-flex items-center gap-2 text-muted-foreground">
            <span className="h-2 w-2 shrink-0 rounded-full bg-red-500" aria-hidden />
            <span className="font-semibold tabular-nums text-foreground">{agg.fail}</span>
            <span>unsuccessful</span>
          </span>
          <span className="inline-flex items-center gap-2 text-muted-foreground">
            <BarChart3 className="h-4 w-4 shrink-0 text-violet-600 opacity-80 dark:text-violet-400" aria-hidden />
            {agg.total > 0 ? (
              <>
                <span className="font-semibold tabular-nums text-foreground">{formatPct(agg.rate)}</span>
                <span>success rate</span>
              </>
            ) : (
              <span className="text-muted-foreground">No attempts yet</span>
            )}
          </span>
        </div>
      </div>

      <div className="border-t border-border/50">
        {entry.contacts.length === 0 ? (
          <p className="px-6 py-10 text-center text-sm text-muted-foreground sm:px-8">
            No contact numbers stored for this BIN / PCN.
          </p>
        ) : visibleContacts.length === 0 ? (
          <p className="px-6 py-10 text-center text-sm text-muted-foreground sm:px-8">
            {blockedContactCount} blocked number{blockedContactCount === 1 ? "" : "s"} hidden. Use{" "}
            <span className="font-medium text-foreground">Show blocked</span> below to list them in
            this table.
          </p>
        ) : (
          <div className="overflow-x-auto">
            {actionError ? (
              <p className="border-b border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive sm:px-6">
                {actionError}
              </p>
            ) : null}
            <Table>
              <TableHeader>
                <TableRow className="border-border/60 hover:bg-transparent">
                  <TableHead className="h-14 min-w-[13rem] px-6 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Phone number
                  </TableHead>
                  <TableHead className="h-14 w-[7.5rem] px-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Quality
                  </TableHead>
                  <TableHead className="h-14 min-w-[7rem] px-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Success rate
                  </TableHead>
                  <TableHead className="h-14 min-w-[8.5rem] px-4 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Stats
                  </TableHead>
                  <TableHead className="h-14 min-w-[9rem] px-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Contact payer
                  </TableHead>
                  <TableHead className="h-14 min-w-[8.5rem] px-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Block / Unblock
                  </TableHead>
                  <TableHead className="h-14 w-12 px-3 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleContacts.map((c, i) => {
                  const r = rowAttempts(c);
                  const q = qualityFromRate(r.rate);
                  const phone = c.phoneNumber?.trim() || "";
                  const isBlocked = Boolean(c.blocked);
                  return (
                    <TableRow
                      key={`${c.phoneNumber}-${i}`}
                      className="border-border/50 hover:bg-muted/25"
                    >
                      <TableCell className="px-6 py-5 align-middle">
                        <div className="flex items-center gap-3">
                          <span
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-500/10 text-violet-600 dark:bg-violet-950/80 dark:text-violet-300"
                            aria-hidden
                          >
                            <Phone className="h-4 w-4" strokeWidth={2} />
                          </span>
                          <span
                            className={cn(
                              "font-mono text-sm font-bold tracking-tight",
                              phoneQualityClass[q],
                            )}
                          >
                            {phone || "—"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-5 align-middle">
                        <QualityBadge q={q} />
                      </TableCell>
                      <TableCell className="px-4 py-5 align-middle">
                        <div className="flex flex-col gap-1">
                          <span className="text-sm font-bold tabular-nums text-foreground">
                            {formatPct(r.rate)}
                          </span>
                          {r.total > 0 ? (
                            <span className="text-xs tabular-nums text-muted-foreground">
                              {r.succ} / {r.total} attempts
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">No attempts</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-5 align-middle">
                        <div className="flex items-start justify-center gap-8">
                          <div className="text-center">
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                              Succ.
                            </div>
                            <div className="mt-1 text-sm font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                              {r.succ}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                              Unsuc.
                            </div>
                            <div className="mt-1 text-sm font-bold tabular-nums text-red-600 dark:text-red-400">
                              {r.fail}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-5 align-middle text-sm text-foreground/90">
                        {c.payerName?.trim() || "—"}
                      </TableCell>
                      <TableCell className="px-4 py-5 align-middle">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className={cn(
                            "gap-1.5 border font-semibold shadow-sm",
                            isBlocked
                              ? "border-emerald-500/50 bg-emerald-500/[0.14] text-emerald-900 hover:bg-emerald-500/25 dark:border-emerald-500/40 dark:bg-emerald-950/50 dark:text-emerald-100 dark:hover:bg-emerald-900/55"
                              : "border-rose-500/50 bg-rose-500/[0.12] text-rose-900 hover:bg-rose-500/22 dark:border-rose-500/40 dark:bg-rose-950/45 dark:text-rose-100 dark:hover:bg-rose-900/50",
                          )}
                          disabled={!phone || pendingPhone === phone}
                          onClick={() => void toggleBlocked(phone, isBlocked)}
                        >
                          {pendingPhone === phone ? (
                            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
                          ) : isBlocked ? (
                            <Unlock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          ) : (
                            <Ban className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          )}
                          {isBlocked ? "Unblock" : "Block"}
                        </Button>
                      </TableCell>
                      <TableCell className="px-3 py-5 text-right align-middle">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              aria-label={`Actions for ${phone || "number"}`}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem
                              disabled={!phone}
                              onSelect={() => copyToClipboard(phone)}
                            >
                              Copy phone number
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {blockedContactCount > 0 ? (
        <div className="flex flex-col gap-2 border-t border-border/50 bg-muted/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <p className="text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-2">
              <Ban className="h-4 w-4 shrink-0 text-rose-600/90 dark:text-rose-400" aria-hidden />
              <span>
                <span className="font-semibold tabular-nums text-foreground">{blockedContactCount}</span>{" "}
                blocked number{blockedContactCount === 1 ? "" : "s"}
                {!showBlockedInSection ? " — hidden from the table above" : " — shown in the table above"}
              </span>
            </span>
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(
              "shrink-0 gap-1.5 self-start text-xs font-medium sm:self-auto",
              showBlockedInSection &&
                "border-violet-500/50 bg-violet-500/[0.12] text-violet-900 dark:border-violet-500/40 dark:bg-violet-950/40 dark:text-violet-100",
            )}
            onClick={() => setShowBlockedInSection((v) => !v)}
            aria-pressed={showBlockedInSection}
          >
            {showBlockedInSection ? "Hide blocked" : "Show blocked"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default function PayerDirectoryPage() {
  const [payerInput, setPayerInput] = useState("");
  const [binInput, setBinInput] = useState("");
  const [pcnInput, setPcnInput] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState<PbmDirectoryQuery>({});

  const [entries, setEntries] = useState<PbmDirectoryEntry[]>([]);
  const [meta, setMeta] = useState<{
    count: number;
    scanned?: number;
    truncated?: boolean;
  }>({ count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [qualityFilter, setQualityFilter] = useState<PbmDirectoryQualityFilter>("all");

  const fetchSeq = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQuery({
        payer_name: payerInput.trim() || undefined,
        bin: binInput.trim() || undefined,
        pcn: pcnInput.trim() || undefined,
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [payerInput, binInput, pcnInput]);

  const load = useCallback(async () => {
    const seq = ++fetchSeq.current;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPbmDirectory(debouncedQuery);
      if (seq !== fetchSeq.current) return;
      if (!data.success) {
        setError(data.error || "Request failed");
        setEntries([]);
        setMeta({ count: 0 });
        return;
      }
      setEntries(data.entries ?? []);
      setMeta({
        count: data.count ?? 0,
        scanned: data.scanned_bin_documents,
        truncated: data.truncated,
      });
    } catch (e) {
      if (seq !== fetchSeq.current) return;
      setError(e instanceof Error ? e.message : "Failed to load");
      setEntries([]);
      setMeta({ count: 0 });
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [debouncedQuery]);

  useEffect(() => {
    void load();
  }, [load]);

  const summaryLine = useMemo(() => {
    if (loading) return "Loading…";
    const parts = [`${meta.count} BIN / PCN row${meta.count === 1 ? "" : "s"}`];
    if (meta.scanned != null) parts.push(`${meta.scanned} BIN document(s) scanned`);
    if (meta.truncated) parts.push("scan capped — narrow filters or set BIN");
    return parts.join(" · ");
  }, [loading, meta]);

  const qualityCounts = useMemo(() => {
    let high = 0;
    let medium = 0;
    let low = 0;
    for (const e of entries) {
      const q = entryOverallQuality(e);
      if (q === "high") high += 1;
      else if (q === "medium") medium += 1;
      else if (q === "low") low += 1;
    }
    return { high, medium, low, all: entries.length };
  }, [entries]);

  const displayedEntries = useMemo(() => {
    const sorted = [...entries].sort(comparePbmEntriesByAttempts);
    if (qualityFilter === "all") return sorted;
    return sorted.filter((e) => entryOverallQuality(e) === qualityFilter);
  }, [entries, qualityFilter]);

  const payerGroups = useMemo(() => buildPayerGroups(displayedEntries), [displayedEntries]);

  const exportJson = useCallback(() => {
    if (displayedEntries.length === 0) return;
    const blob = new Blob([JSON.stringify(displayedEntries, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pbm-directory-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [displayedEntries]);

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">PBM directory</h1>
          <p className="text-sm text-muted-foreground">
            Rows are grouped by identical PCN-level payer name. The group header updates every BIN / PCN
            in that group; each card has Edit under “PCN payer name” to change only that BIN / PCN.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1"
            disabled={entries.length === 0 || displayedEntries.length === 0 || loading}
            onClick={exportJson}
          >
            <Download className="h-3.5 w-3.5" aria-hidden />
            Export data
          </Button>
          <Button
            type="button"
            size="sm"
            className="gap-1 bg-violet-600 text-white hover:bg-violet-700 dark:bg-violet-600 dark:hover:bg-violet-500"
            onClick={() => void load()}
            disabled={loading}
          >
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} aria-hidden />
            Refresh
          </Button>
        </div>
      </div>

      <section
        className="grid gap-4 rounded-lg border border-border bg-card p-4"
        aria-label="Directory filters"
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5 sm:col-span-2">
            <label
              htmlFor="pbm-payer"
              className="text-sm font-medium leading-none"
            >
              Payer name contains
            </label>
            <Input
              id="pbm-payer"
              value={payerInput}
              onChange={(e) => setPayerInput(e.target.value)}
              placeholder="e.g. CVS, Optum…"
              autoComplete="off"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="pbm-bin" className="text-sm font-medium leading-none">
              Member BIN
            </label>
            <Input
              id="pbm-bin"
              value={binInput}
              onChange={(e) => setBinInput(e.target.value)}
              placeholder="Exact document id"
              autoComplete="off"
              className="font-mono text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="pbm-pcn" className="text-sm font-medium leading-none">
              Member PCN
            </label>
            <Input
              id="pbm-pcn"
              value={pcnInput}
              onChange={(e) => setPcnInput(e.target.value)}
              placeholder="Exact field key"
              autoComplete="off"
              className="font-mono text-sm"
            />
          </div>
        </div>
        <p
          className="border-t border-border/80 pt-3 text-xs text-muted-foreground"
          role="status"
          aria-live="polite"
        >
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
              {summaryLine}
            </span>
          ) : (
            summaryLine
          )}
        </p>
      </section>

      {entries.length > 0 ? (
        <div className="flex flex-wrap gap-2" role="toolbar" aria-label="Filter cards by overall quality">
          <button
            type="button"
            onClick={() => setQualityFilter("all")}
            className={cn(
              "rounded-md border px-3 py-1.5 text-xs font-medium",
              qualityFilter === "all"
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-background hover:bg-muted/50",
            )}
          >
            All <span className="ml-1 tabular-nums">{qualityCounts.all}</span>
          </button>
          <button
            type="button"
            onClick={() => setQualityFilter("high")}
            className={cn(
              "rounded-md border px-3 py-1.5 text-xs font-medium",
              qualityFilter === "high"
                ? "border-primary bg-primary text-primary-foreground"
                : "border-emerald-200 bg-emerald-50 text-emerald-900 hover:bg-emerald-100 dark:border-emerald-900/40 dark:bg-emerald-950/50 dark:text-emerald-200 dark:hover:bg-emerald-950/80",
            )}
          >
            High <span className="ml-1 tabular-nums">{qualityCounts.high}</span>
          </button>
          <button
            type="button"
            onClick={() => setQualityFilter("medium")}
            className={cn(
              "rounded-md border px-3 py-1.5 text-xs font-medium",
              qualityFilter === "medium"
                ? "border-primary bg-primary text-primary-foreground"
                : "border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-900/40 dark:bg-amber-950/50 dark:text-amber-200 dark:hover:bg-amber-950/80",
            )}
          >
            Medium <span className="ml-1 tabular-nums">{qualityCounts.medium}</span>
          </button>
          <button
            type="button"
            onClick={() => setQualityFilter("low")}
            className={cn(
              "rounded-md border px-3 py-1.5 text-xs font-medium",
              qualityFilter === "low"
                ? "border-primary bg-primary text-primary-foreground"
                : "border-red-200 bg-red-50 text-red-900 hover:bg-red-100 dark:border-red-900/40 dark:bg-red-950/50 dark:text-red-200 dark:hover:bg-red-950/80",
            )}
          >
            Low <span className="ml-1 tabular-nums">{qualityCounts.low}</span>
          </button>
        </div>
      ) : null}

      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && !error && entries.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border/80 bg-card/50 px-6 py-16 text-center shadow-sm sm:px-8">
          <p className="text-base font-semibold text-foreground">No matching BIN / PCN rows</p>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            Try clearing filters or widening the payer name search. If you filtered by BIN, confirm
            that document exists in Firestore.
          </p>
        </div>
      ) : null}

      {!loading && !error && entries.length > 0 && displayedEntries.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No BIN / PCN rows match this overall quality filter (N/A rows only appear when All is
          selected).
        </p>
      ) : null}

      {displayedEntries.length > 0 ? (
        <div className="flex flex-col gap-8">
          {payerGroups.map((g) => (
            <PayerNameGroupBlock
              key={g.groupKey || "__unnamed__"}
              group={g}
              onRefresh={load}
            />
          ))}
        </div>
      ) : null}

      {loading && entries.length === 0 ? (
        <div className="flex items-center justify-center gap-3 rounded-lg border border-dashed border-border px-4 py-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
          Loading directory…
        </div>
      ) : null}
    </div>
  );
}
